/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import get from 'lodash/get';
import moment from 'moment';
import ExcelJS from 'exceljs';
import {
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  DatePicker,
  Pagination,
  Upload,
  notification,
  Row,
  Col,
} from 'antd';
import { Button as CrceButton, Button } from '@crc-evans/ui-components';
import { Plus, Upload as UploadIcon, Download, FileSpreadsheet, Factory } from 'lucide-react';
import FabricationModernDataGrid from './FabricationModernDataGrid';
import { useHeatStyleColumnFacets } from '../technical/heatStyleColumnFacets';
import {
  computeFabricationDerivedFields,
  FABRICATION_ON_TIME_FLAG_OPTIONS,
  formatPercent,
} from '../../utils/fabricationJobCalculations';
import {
  FABRICATION_EXCEL_HEADERS,
  applyFabricationTemplateValidations,
} from '../../utils/fabricationExcel';
import {
  mapFabricationJobForUi,
  toFabricationApiPayload,
} from '../../utils/fabricationApiMapper';
import {
  fetchFabricationJobs,
  createFabricationJob,
  updateFabricationJob,
  deleteFabricationJob,
} from '../../redux/fabricationJobs/action';
import {
  onUploadFabricationExcel,
  downloadFabricationJobsFromApi,
} from '../../redux/fabricationJobs/saga';
import '../DPR/DPRSettingsSparesTab.css';
import './FabricationDashboard.css';

const FABRICATION_FACET_KEYS = ['projectNo', 'client', 'sector', 'site', 'deliveryStatus'];

const FABRICATION_FILTERS_EMPTY = FABRICATION_FACET_KEYS.reduce((acc, k) => {
  acc[k] = '';
  return acc;
}, {});

const MONTH_OPTIONS = moment.monthsShort().map((m) => ({ label: m, value: m }));

const toApiDate = (v) => {
  if (!v) return null;
  const m = moment(v);
  return m.isValid() ? m.format('YYYY-MM-DD') : null;
};

const fromApiDate = (v) => {
  if (!v) return null;
  const m = moment(v);
  return m.isValid() ? m : null;
};

const FabricationDashboard = ({
  selectedProject,
  projectTeamMembersAccessList,
  userAccessList,
  jobsList,
  isJobsListLoading,
  onFetchFabricationJobs,
  onCreateFabricationJob,
  onUpdateFabricationJob,
  onDeleteFabricationJob,
}) => {
  const [jobForm] = Form.useForm();
  const [showJobModal, setShowJobModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [openUploadModal, setOpenUploadModal] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [heatColumnFilters, setHeatColumnFilters] = useState(() => ({ ...FABRICATION_FILTERS_EMPTY }));
  const [columnFilters, setColumnFilters] = useState({});
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [current, setCurrent] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [derivedPreview, setDerivedPreview] = useState({});

  const resolveUserGroups = () => {
    const fromProps = Array.isArray(userAccessList) ? userAccessList : [];
    if (fromProps.length > 0) return fromProps;
    try {
      const raw = localStorage.getItem('UserGroup');
      if (raw == null || raw === '') return [];
      try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return String(raw)
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);
      }
    } catch {
      return [];
    }
  };

  const isGlobalAdminGroup = (groups) =>
    Array.isArray(groups) &&
    groups.some((g) => {
      const n = String(g || '').toLowerCase();
      return n === 'admin' || n === 'super-admin' || n === 'administrator';
    });

  const isReadOnly = () => {
    const groups = resolveUserGroups();
    if (isGlobalAdminGroup(groups)) return false;
    if (projectTeamMembersAccessList?.[0]?.isGuestUser) return true;
    if (
      projectTeamMembersAccessList?.[0]?.isNormalUser &&
      !(
        projectTeamMembersAccessList?.[0]?.tabPermissions?.Production?.write ||
        projectTeamMembersAccessList?.[0]?.tabPermissions?.Fabrication?.write
      )
    ) {
      return true;
    }
    return false;
  };

  const readOnly = isReadOnly();

  const guardWrite = () => {
    if (readOnly) {
      notification.warning({
        message: 'Permission required',
        description: 'You do not have permission to modify fabrication jobs for this project.',
      });
      return false;
    }
    return true;
  };

  useEffect(() => {
    if (selectedProject?.id) {
      onFetchFabricationJobs({ projectId: selectedProject.id });
      setHeatColumnFilters({ ...FABRICATION_FILTERS_EMPTY });
      setColumnFilters({});
      setCurrent(1);
    }
  }, [selectedProject?.id, onFetchFabricationJobs]);

  const enrichedJobs = useMemo(
    () => (Array.isArray(jobsList) ? jobsList.map(mapFabricationJobForUi) : []),
    [jobsList]
  );

  const jobsFacetRef = useRef([]);
  jobsFacetRef.current = enrichedJobs;

  const handleHeatFilterChange = useCallback((key, value) => {
    setHeatColumnFilters((prev) => ({ ...prev, [key]: value }));
    setCurrent(1);
  }, []);

  const fetchFacetRows = useCallback(async () => {
    const list = jobsFacetRef.current;
    return Array.isArray(list) ? [...list] : [];
  }, []);

  const { applyFacetClientFilter, renderTextColumnFilterTrigger } = useHeatStyleColumnFacets({
    enabled: Boolean(selectedProject?.id),
    filterableColumns: FABRICATION_FACET_KEYS,
    listCount: enrichedJobs.length,
    projectList: enrichedJobs,
    searchCriteria: useMemo(() => ({}), []),
    columnFilters: heatColumnFilters,
    onColumnFilterChange: handleHeatFilterChange,
    fetchFacetRows,
  });

  const filteredRows = useMemo(() => {
    let rows = [...enrichedJobs];
    Object.keys(columnFilters).forEach((key) => {
      const raw = (columnFilters[key] || '').trim().toLowerCase();
      if (!raw) return;
      rows = rows.filter((r) => String(get(r, key) ?? '').toLowerCase().includes(raw));
    });
    FABRICATION_FACET_KEYS.forEach((key) => {
      const raw = (heatColumnFilters[key] || '').trim().toLowerCase();
      if (!raw) return;
      rows = rows.filter((r) => String(get(r, key) ?? '').toLowerCase().includes(raw));
    });
    return applyFacetClientFilter(rows);
  }, [enrichedJobs, columnFilters, heatColumnFilters, applyFacetClientFilter]);

  const sortedRows = useMemo(() => {
    if (!sortKey) return filteredRows;
    const desc = sortDir === 'desc';
    return [...filteredRows].sort((a, b) => {
      let av = a[sortKey];
      let bv = b[sortKey];
      if (sortKey.includes('Date')) {
        av = av ? moment(av).valueOf() : 0;
        bv = bv ? moment(bv).valueOf() : 0;
        return desc ? bv - av : av - bv;
      }
      if (typeof av === 'number' && typeof bv === 'number') {
        return desc ? bv - av : av - bv;
      }
      av = av == null ? '' : String(av);
      bv = bv == null ? '' : String(bv);
      const cmp = av.localeCompare(bv, undefined, { numeric: true });
      return desc ? -cmp : cmp;
    });
  }, [filteredRows, sortKey, sortDir]);

  const pagedRows = useMemo(() => {
    const start = (current - 1) * pageSize;
    return sortedRows.slice(start, start + pageSize);
  }, [sortedRows, current, pageSize]);

  useEffect(() => {
    const lastPage = Math.max(1, Math.ceil(sortedRows.length / pageSize) || 1);
    if (current > lastPage) setCurrent(lastPage);
  }, [sortedRows.length, pageSize, current]);

  const toggleSort = (key) => {
    setSortKey((prev) => {
      if (prev === key) {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        return prev;
      }
      setSortDir('asc');
      return key;
    });
  };

  const handleColumnFilterChange = (key, value) => {
    setColumnFilters((prev) => ({ ...prev, [key]: value }));
    setCurrent(1);
  };

  const buildDerivedInput = (values) => ({
    quotedHours: values.quotedHours,
    actualHours: values.actualHours,
    onTimeFlag: values.onTimeFlag,
    promisedDeliveryDate: values.promisedDeliveryDate,
    actualDeliveryDate: values.actualDeliveryDate,
    totalWeldedJoints: values.totalWeldedJoints,
    totalJointsRejected: values.totalJointsRejected,
  });

  const updateDerivedPreview = (valuesOverride) => {
    const values = valuesOverride ?? jobForm.getFieldsValue();
    setDerivedPreview(computeFabricationDerivedFields(buildDerivedInput(values)));
  };

  const openAddModal = () => {
    if (!guardWrite()) return;
    setIsEditMode(false);
    setEditingId(null);
    jobForm.resetFields();
    setDerivedPreview({});
    setShowJobModal(true);
  };

  const openEditModal = (row) => {
    if (!guardWrite()) return;
    setIsEditMode(true);
    setEditingId(row.id);
    const formValues = {
      projectNo: row.projectNo,
      client: row.client,
      sector: row.sector,
      promisedMonth: row.promisedMonth,
      quotedHours: row.quotedHours,
      actualHours: row.actualHours,
      site: row.site,
      onTimeFlag:
        row.onTimeFlag != null
          ? Number(row.onTimeFlag)
          : row.deliveryStatus === 'On Time'
            ? 1
            : row.deliveryStatus === 'Late'
              ? 0
              : undefined,
      promisedDeliveryDate: fromApiDate(row.promisedDeliveryDate),
      actualDeliveryDate: fromApiDate(row.actualDeliveryDate),
      totalWeldedJoints: row.totalWeldedJoints,
      totalJointsRejected: row.totalJointsRejected,
    };
    jobForm.setFieldsValue(formValues);
    /* setFieldsValue is async — pass values directly so variance preview is populated on open */
    updateDerivedPreview(formValues);
    setShowJobModal(true);
  };

  const buildPayloadFromForm = (values) =>
    toFabricationApiPayload({
      projectNo: values.projectNo,
      client: values.client,
      sector: values.sector,
      promisedMonth: values.promisedMonth,
      quotedHours: values.quotedHours,
      actualHours: values.actualHours,
      site: values.site,
      onTimeFlag: values.onTimeFlag,
      promisedDeliveryDate: toApiDate(values.promisedDeliveryDate),
      actualDeliveryDate: toApiDate(values.actualDeliveryDate),
      totalWeldedJoints: values.totalWeldedJoints,
      totalJointsRejected: values.totalJointsRejected,
    });

  const handleSaveJob = () => {
    jobForm.validateFields().then((values) => {
      const payload = buildPayloadFromForm(values);
      if (isEditMode && editingId != null) {
        onUpdateFabricationJob({
          payload: { ...payload, id: editingId },
          selectedProject,
        });
      } else {
        onCreateFabricationJob({ payload, selectedProject });
      }
      setShowJobModal(false);
    });
  };

  const confirmDelete = () => {
    if (!deleteTarget?.id) return;
    onDeleteFabricationJob({ id: deleteTarget.id, selectedProject });
    setDeleteTarget(null);
  };

  const downloadWorkbook = async (rows, filename, headersOnly = false) => {
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet('Data');
    sheet.columns = FABRICATION_EXCEL_HEADERS.map((h) => ({ header: h, key: h, width: 18 }));
    const headerRow = sheet.getRow(1);
    headerRow.eachCell((cell) => {
      cell.font = { bold: true };
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE8F0F8' },
      };
    });
    if (headersOnly) {
      applyFabricationTemplateValidations(sheet);
    }
    if (!headersOnly) {
      rows.forEach((row) => {
        sheet.addRow({
          'Project No': row.projectNo,
          Client: row.client,
          Sector: row.sector,
          'Promised Month': row.promisedMonth,
          'Quoted Hours': row.quotedHours,
          'Actual Hours': row.actualHours,
          'On-Time Flag': row.onTimeFlag != null ? Number(row.onTimeFlag) : '',
          Site: row.site,
          'Promised Delivery Date': row.promisedDeliveryDate
            ? moment(row.promisedDeliveryDate).format('DD MMM YYYY')
            : '',
          'Actual Delivery Date': row.actualDeliveryDate
            ? moment(row.actualDeliveryDate).format('DD MMM YYYY')
            : '',
          'Total Welded Joints': row.totalWeldedJoints,
          'Total Joints Rejected': row.totalJointsRejected,
        });
      });
    }
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadTemplate = () => downloadWorkbook([], 'fabrication-jobs-template.xlsx', true);

  const handleExport = async () => {
    if (selectedProject?.id) {
      const ok = await downloadFabricationJobsFromApi(selectedProject.id);
      if (ok) return;
    }
    downloadWorkbook(enrichedJobs, 'fabrication-jobs-export.xlsx', false);
  };

  const handleUpload = async () => {
    if (!guardWrite()) return;
    const file = fileList[0]?.originFileObj || fileList[0];
    if (!file || !selectedProject?.id) {
      notification.warning({ message: 'Please select an Excel file to upload.' });
      return;
    }
    setUploading(true);
    const ok = await onUploadFabricationExcel(file, selectedProject.id);
    setUploading(false);
    if (ok) {
      setOpenUploadModal(false);
      setFileList([]);
      onFetchFabricationJobs({ projectId: selectedProject.id });
    }
  };

  return (
    <div className="fabrication-dashboard-tab">
      <div className="fabrication-dashboard-card">
        <div className="fabrication-dashboard-header">
          <div className="fabrication-dashboard-header-left">
            <div className="fabrication-dashboard-icon-wrap" aria-hidden>
              <Factory size={24} />
            </div>
            <div className="fabrication-dashboard-header-content">
              <div className="fabrication-dashboard-title-row">
                <h3 className="fabrication-dashboard-title">Production jobs</h3>
                <span className="fabrication-dashboard-summary">
                  {enrichedJobs.length} total
                </span>
              </div>
              <p className="fabrication-dashboard-subtitle">
                Track hours, delivery, and weld quality for jobs on this project.
              </p>
            </div>
          </div>
          <div className="fabrication-dashboard-actions">
            <CrceButton
              label="Add Production Job"
              variant="primary"
              size="medium"
              icon={Plus}
              onClick={openAddModal}
              disabled={readOnly}
            />
            <CrceButton
              label="Upload Excel"
              variant="secondary"
              size="medium"
              icon={UploadIcon}
              onClick={() => {
                if (guardWrite()) setOpenUploadModal(true);
              }}
              disabled={readOnly}
            />
            <CrceButton
              label="Template"
              variant="tertiary"
              size="medium"
              icon={FileSpreadsheet}
              onClick={handleDownloadTemplate}
            />
            <CrceButton
              label="Export"
              variant="tertiary"
              size="medium"
              icon={Download}
              onClick={handleExport}
            />
          </div>
        </div>

        <FabricationModernDataGrid
          sortedRows={pagedRows}
          isLoading={isJobsListLoading}
          current={current}
          pageSize={pageSize}
          sortKey={sortKey}
          sortDir={sortDir}
          onToggleSort={toggleSort}
          columnFilters={columnFilters}
          onColumnFilterChange={handleColumnFilterChange}
          renderTextColumnFilterTrigger={renderTextColumnFilterTrigger}
          onEdit={openEditModal}
          onDelete={(row) => {
            if (guardWrite()) setDeleteTarget(row);
          }}
          readOnly={readOnly}
        />

        {!isJobsListLoading && sortedRows.length > 0 && (
          <div className="dpr-settings-spares-pagination fabrication-pagination">
            <Pagination
              className="heat-profile-pagination"
              current={current}
              pageSize={pageSize}
              total={sortedRows.length}
              showSizeChanger
              showTotal={(total, range) => `${range[0]}-${range[1]} of ${total}`}
              pageSizeOptions={['10', '20', '50', '100']}
              onChange={(page, size) => {
                setCurrent(page);
                setPageSize(size);
              }}
            />
          </div>
        )}
      </div>

      <Modal
        title={isEditMode ? 'Edit Production Job' : 'Add Production Job'}
        visible={showJobModal}
        centered
        width={800}
        destroyOnClose
        maskClosable={false}
        afterOpenChange={(open) => {
          if (open) {
            requestAnimationFrame(() => updateDerivedPreview());
          }
        }}
        wrapClassName="fabrication-ant-modal-wrap"
        className="fabrication-form-modal fabrication-modern-modal"
        onCancel={() => setShowJobModal(false)}
        footer={
          <div className="fabrication-modal-footer">
            <Button label="Cancel" variant="tertiary" size="medium" onClick={() => setShowJobModal(false)} />
            <Button
              label={isEditMode ? 'Update' : 'Save'}
              variant="primary"
              size="medium"
              onClick={handleSaveJob}
            />
          </div>
        }
      >
        <Form
          form={jobForm}
          layout="vertical"
          className="fabrication-modal-form"
          onValuesChange={updateDerivedPreview}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Project No"
                name="projectNo"
                rules={[{ required: true, message: 'Project No is required' }]}
              >
                <Input
                  className="fabrication-modal-input"
                  size="large"
                  placeholder="e.g. Burnley-001"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Client"
                name="client"
                rules={[{ required: true, message: 'Client is required' }]}
              >
                <Input className="fabrication-modal-input" size="large" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Sector" name="sector">
                <Input className="fabrication-modal-input" size="large" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Promised Month" name="promisedMonth">
                <Select
                  allowClear
                  size="large"
                  options={MONTH_OPTIONS}
                  placeholder="Select month"
                  className="fabrication-modal-select"
                  dropdownClassName="modern-dropdown-popup fabrication-select-dropdown"
                  getPopupContainer={() => document.body}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Quoted Hours"
                name="quotedHours"
                rules={[{ required: true, message: 'Quoted hours is required' }]}
              >
                <InputNumber
                  className="fabrication-modal-input-number"
                  min={0}
                  size="large"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Actual Hours"
                name="actualHours"
                rules={[{ required: true, message: 'Actual hours is required' }]}
              >
                <InputNumber
                  className="fabrication-modal-input-number"
                  min={0}
                  size="large"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Site" name="site">
                <Input className="fabrication-modal-input" size="large" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Delivery Status" name="onTimeFlag">
                <Select
                  allowClear
                  size="large"
                  options={FABRICATION_ON_TIME_FLAG_OPTIONS}
                  placeholder="Select status"
                  className="fabrication-modal-select"
                  dropdownClassName="modern-dropdown-popup fabrication-select-dropdown"
                  getPopupContainer={() => document.body}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Promised Delivery Date"
                name="promisedDeliveryDate"
                rules={[{ required: true, message: 'Promised delivery date is required' }]}
              >
                <DatePicker
                  className="fabrication-modal-datepicker"
                  size="large"
                  style={{ width: '100%' }}
                  format="DD MMM YYYY"
                  placeholder="Select date"
                  dropdownClassName="fabrication-datepicker-dropdown"
                  getPopupContainer={() => document.body}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Actual Delivery Date" name="actualDeliveryDate">
                <DatePicker
                  className="fabrication-modal-datepicker"
                  size="large"
                  style={{ width: '100%' }}
                  format="DD MMM YYYY"
                  placeholder="Select date"
                  dropdownClassName="fabrication-datepicker-dropdown"
                  getPopupContainer={() => document.body}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Total Welded Joints" name="totalWeldedJoints">
                <InputNumber
                  className="fabrication-modal-input-number"
                  min={0}
                  size="large"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Total Joints Rejected" name="totalJointsRejected">
                <InputNumber
                  className="fabrication-modal-input-number"
                  min={0}
                  size="large"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
        <div className="fabrication-modal-derived" aria-live="polite">
          <span>
            Hours variance:{' '}
            <strong>
              {derivedPreview.hoursVariance != null ? derivedPreview.hoursVariance : '—'}
            </strong>
          </span>
          <span>
            Hrs variance %: <strong>{formatPercent(derivedPreview.hrsVariancePct, 0)}</strong>
          </span>
          <span>
            Delivery status: <strong>{derivedPreview.deliveryStatus ?? '—'}</strong>
          </span>
          <span>
            Days variance:{' '}
            <strong>
              {derivedPreview.deliveryDaysVariance != null
                ? derivedPreview.deliveryDaysVariance
                : '—'}
            </strong>
          </span>
          <span>
            Reject rate: <strong>{formatPercent(derivedPreview.rejectRatePct, 1)}</strong>
          </span>
        </div>
      </Modal>

      <Modal
        title="Delete fabrication job"
        visible={Boolean(deleteTarget)}
        centered
        destroyOnClose
        wrapClassName="fabrication-ant-modal-wrap"
        className="fabrication-delete-modal fabrication-modern-modal"
        onCancel={() => setDeleteTarget(null)}
        footer={
          <div className="fabrication-modal-footer">
            <Button label="Cancel" variant="tertiary" size="medium" onClick={() => setDeleteTarget(null)} />
            <Button label="Delete" variant="primary" size="medium" onClick={confirmDelete} />
          </div>
        }
      >
        <p className="fabrication-delete-modal-copy">
          Delete job <strong>{deleteTarget?.projectNo}</strong>? This cannot be undone.
        </p>
      </Modal>

      <Modal
        title="Upload fabrication Excel"
        visible={openUploadModal}
        centered
        destroyOnClose
        wrapClassName="fabrication-ant-modal-wrap"
        className="fabrication-upload-modal fabrication-modern-modal"
        onCancel={() => {
          setOpenUploadModal(false);
          setFileList([]);
        }}
        footer={
          <div className="fabrication-modal-footer">
            <Button
              label="Cancel"
              variant="tertiary"
              size="medium"
              onClick={() => {
                setOpenUploadModal(false);
                setFileList([]);
              }}
            />
            <Button
              label={uploading ? 'Uploading…' : 'Upload'}
              variant="primary"
              size="medium"
              onClick={handleUpload}
              disabled={uploading}
            />
          </div>
        }
      >
        <p className="fabrication-upload-hint">
          Upload the <strong>Data</strong> sheet from your workbook. Calculated columns are ignored;
          the server will store editable fields only.
        </p>
        <Upload
          accept=".xlsx,.xls"
          fileList={fileList}
          beforeUpload={() => false}
          onChange={({ fileList: fl }) => setFileList(fl.slice(-1))}
          maxCount={1}
        >
          <CrceButton label="Select file" variant="secondary" size="medium" icon={UploadIcon} />
        </Upload>
      </Modal>
    </div>
  );
};

const mapStateToProps = ({ fabricationJobsReducer }) => ({
  jobsList: get(fabricationJobsReducer, 'jobsList', []),
  isJobsListLoading: get(fabricationJobsReducer, 'isJobsListLoading', false),
});

const mapDispatchToProps = {
  onFetchFabricationJobs: fetchFabricationJobs,
  onCreateFabricationJob: createFabricationJob,
  onUpdateFabricationJob: updateFabricationJob,
  onDeleteFabricationJob: deleteFabricationJob,
};

export default connect(mapStateToProps, mapDispatchToProps)(FabricationDashboard);
